"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { apiFetch } from "../utils/apiFetch";

interface FeatureFlagContextType {
  flags: Record<string, boolean>;
  isLoaded: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType>({
  flags: {},
  isLoaded: false,
});

export function FeatureFlagProvider({
  children,
  spaceName,
}: {
  children: ReactNode;
  spaceName?: string;
}) {
  const { isReady, getToken } = useAuth();
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!isReady) return;

    let cancelled = false;

    async function fetchFlags(): Promise<boolean> {
      const headers: Record<string, string> = {};
      if (spaceName) {
        headers["X-Space-Name"] = spaceName;
      }
      const token = await getToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const response = await apiFetch("/api/feature-flags", { headers });
      if (response.ok) {
        const data = await response.json();
        if (!cancelled) {
          setFlags(data);
        }
        return true;
      }
      return false;
    }

    async function load() {
      try {
        const success = await fetchFlags();
        if (!success) {
          await new Promise((r) => setTimeout(r, 2000));
          await fetchFlags();
        }
      } catch {
        try {
          await new Promise((r) => setTimeout(r, 2000));
          await fetchFlags();
        } catch {
          // give up
        }
      } finally {
        if (!cancelled) {
          setIsLoaded(true);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [isReady, spaceName, getToken]);

  return (
    <FeatureFlagContext.Provider value={{ flags, isLoaded }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlag(name: string, defaultValue = false): boolean {
  const { flags } = useContext(FeatureFlagContext);
  return flags[name] ?? defaultValue;
}

export function useFeatureFlags(): FeatureFlagContextType {
  return useContext(FeatureFlagContext);
}
